<template>
  <div class="widget-carousel-product-editor">
    <div class="card border-0 shadow-sm compact-form-card">
      <div class="card-header bg-white border-bottom compact-form-card__header">
        <div class="d-flex flex-column">
          <strong>
            Configurare slide:
            {{ product.product_name || 'Fără nume produs' }}
          </strong>

          <div
            v-if="!product.is_active"
            class="widget-carousel-product-editor__inactive-box"
          >
            <span class="badge badge-danger">Inactive</span>
            <div
              v-if="product.reason"
              class="widget-carousel-product-editor__inactive-reason"
            >
              {{ product.reason }}
            </div>
          </div>
        </div>
      </div>

      <div class="card-body compact-form-card__body">
        <div class="widget-carousel-product-editor__section">
          <h5 class="widget-carousel-product-editor__section-title">
            Status produs
          </h5>

          <div class="form-group form-check compact-check-group">
            <input
              :id="`product-is-active-${product.local_id}`"
              type="checkbox"
              class="form-check-input"
              :checked="product.is_active"
              :disabled="true"
              @change="updateProductField('is_active', $event.target.checked)"
            >
            <label
              class="form-check-label compact-check-label"
              :for="`product-is-active-${product.local_id}`"
            >
              Produs activ
            </label>
          </div>

          <div class="form-group compact-form-group mb-0">
            <label class="compact-label">Reason</label>
            <input
              type="text"
              class="form-control compact-input"
              :value="product.reason"
              :disabled="true"
              @input="updateProductField('reason', $event.target.value)"
              placeholder="Motiv indisponibilitate"
            >
          </div>
        </div>

        <div
          v-if="shouldShowProductImageInput"
          class="widget-carousel-product-editor__section"
        >
          <h5 class="widget-carousel-product-editor__section-title">
            Imagine produs
          </h5>

          <div class="form-group compact-form-group">
            <label class="compact-label" :for="`product-image-${product.local_id}`">
              Încarcă imagine
            </label>

            <input
              :id="`product-image-${product.local_id}`"
              :ref="`productImageInput_${product.local_id}`"
              type="file"
              class="form-control-file compact-file-input"
              accept="image/*"
              :disabled="isDisabled"
              @change="onProductImageChange"
            >

            <small class="compact-help-text d-block mt-1">
              Imaginea produsului este afișată doar dacă nu există imagine globală.
            </small>
          </div>

          <div v-if="product.image && product.image.error" class="widget-carousel-product-editor__error">
            {{ product.image.error }}
          </div>

          <div
            v-if="product.image && product.image.preview"
            class="widget-carousel-product-editor__image-preview-wrap"
          >
            <img
              :src="product.image.preview"
              alt="Preview imagine produs"
              class="widget-carousel-product-editor__image-preview"
            >

            <button
              type="button"
              class="btn btn-outline-danger btn-sm mt-2"
              :disabled="isDisabled"
              @click="removeProductImage"
            >
              Elimină imaginea
            </button>
          </div>
        </div>

        <div class="widget-carousel-product-editor__section">
          <h5 class="widget-carousel-product-editor__section-title">
            Link voucher
          </h5>

          <div class="form-group form-check compact-check-group">
            <input
              :id="`voucher-url-default-${product.local_id}`"
              type="checkbox"
              class="form-check-input"
              :checked="isVoucherUrlDefault"
              :disabled="isDisabled"
              @change="onVoucherUrlDefaultChange($event.target.checked)"
            >
            <label
              class="form-check-label compact-check-label"
              :for="`voucher-url-default-${product.local_id}`"
            >
              Folosește URL automat
            </label>
          </div>

          <div class="form-group compact-form-group mb-0">
            <label class="compact-label" :for="`voucher-url-${product.local_id}`">
              Voucher URL
            </label>

            <input
              :id="`voucher-url-${product.local_id}`"
              type="text"
              class="form-control compact-input"
              :value="voucherUrlInputValue"
              :disabled="isDisabled || isVoucherUrlDefault"
              :required="!isVoucherUrlDefault"
              @input="onVoucherUrlInput"
              placeholder="Introdu URL-ul voucher-ului"
            >

            <small class="compact-help-text d-block mt-1">
              Când opțiunea automată este activă, URL-ul se generează pe baza product_id.
            </small>
          </div>
        </div>

        <div class="widget-carousel-product-editor__section mb-0">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="widget-carousel-product-editor__section-title mb-0">
              Badges
            </h5>
          </div>

          <div class="widget-carousel-product-editor__badge-grid">
            <div
              v-for="position in badgePositions"
              :key="position.key"
              class="widget-carousel-product-editor__badge-slot"
            >
              <div class="widget-carousel-product-editor__badge-slot-header">
                <strong>{{ position.label }}</strong>

                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="isDisabled"
                  @click="addBadge(position.key)"
                >
                  Adaugă badge
                </button>
              </div>

              <div v-if="getBadgeList(position.key).length === 0" class="widget-carousel-product-editor__badge-empty">
                Nu există badge-uri pentru această poziție.
              </div>

              <div
                v-for="(badge, badgeIndex) in getBadgeList(position.key)"
                :key="badge.local_id"
                class="widget-carousel-product-editor__badge-item"
              >
                <div class="widget-carousel-product-editor__badge-item-header">
                  <strong>Badge #{{ badgeIndex + 1 }}</strong>

                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    :disabled="isDisabled"
                    @click="removeBadge(position.key, badgeIndex)"
                  >
                    Șterge badge
                  </button>
                </div>

                <div class="widget-carousel-product-editor__badge-group-box">
                  <div class="widget-carousel-product-editor__badge-group-box-title">
                    Config group
                  </div>

                  <div class="form-row compact-form-row">
                    <div class="form-group col-12 col-md-6 compact-form-group">
                      <label class="compact-label">Background color</label>
                      <div class="widget-carousel-product-editor__color-row">
                        <input
                          type="color"
                          class="form-control widget-carousel-product-editor__color-input"
                          :value="normalizeColor(badge.background_color)"
                          :disabled="isDisabled || isTransparentBackground(badge.background_color)"
                          @input="updateBadgeGroupField(position.key, badgeIndex, 'background_color', $event.target.value)"
                        >
                        <input
                          type="text"
                          class="form-control compact-input"
                          :value="badge.background_color"
                          :disabled="isDisabled || isTransparentBackground(badge.background_color)"
                          @input="updateBadgeGroupField(position.key, badgeIndex, 'background_color', $event.target.value)"
                          placeholder="#FFFFFF"
                        >
                      </div>
                    </div>

                    <div class="form-group col-12 col-md-6 compact-form-group">
                      <label class="compact-label d-block">Fundal transparent</label>
                      <div class="form-check compact-check-group mt-2">
                        <input
                          :id="`badge-group-transparent-${product.local_id}-${position.key}-${badgeIndex}`"
                          type="checkbox"
                          class="form-check-input"
                          :checked="isTransparentBackground(badge.background_color)"
                          :disabled="isDisabled"
                          @change="setBadgeGroupTransparent(position.key, badgeIndex, $event.target.checked)"
                        >
                        <label
                          class="form-check-label compact-check-label"
                          :for="`badge-group-transparent-${product.local_id}-${position.key}-${badgeIndex}`"
                        >
                          Transparent
                        </label>
                      </div>
                    </div>
                  </div>

                  <div class="form-row compact-form-row">
                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Corner radius</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="Number(badge.corner_radius) || 12"
                        :disabled="isDisabled"
                        @input="updateBadgeGroupNumber(position.key, badgeIndex, 'corner_radius', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Spacing</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="Number(badge.spacing) || 0"
                        :disabled="isDisabled"
                        @input="updateBadgeGroupNumber(position.key, badgeIndex, 'spacing', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label d-block">Use D shape</label>
                      <div class="form-check compact-check-group mt-2">
                        <input
                          :id="`badge-group-d-shape-${product.local_id}-${position.key}-${badgeIndex}`"
                          type="checkbox"
                          class="form-check-input"
                          :checked="badge.use_d_shape"
                          :disabled="isDisabled"
                          @change="updateBadgeGroupField(position.key, badgeIndex, 'use_d_shape', $event.target.checked)"
                        >
                        <label
                          class="form-check-label compact-check-label"
                          :for="`badge-group-d-shape-${product.local_id}-${position.key}-${badgeIndex}`"
                        >
                          Active
                        </label>
                      </div>
                    </div>
                  </div>

                  <div class="form-row compact-form-row">
                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Padding top</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeGroupPaddingValue(badge, 'top')"
                        :disabled="isDisabled"
                        @input="updateBadgeGroupPadding(position.key, badgeIndex, 'top', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Padding left</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeGroupPaddingValue(badge, 'left')"
                        :disabled="isDisabled"
                        @input="updateBadgeGroupPadding(position.key, badgeIndex, 'left', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Padding right</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeGroupPaddingValue(badge, 'right')"
                        :disabled="isDisabled"
                        @input="updateBadgeGroupPadding(position.key, badgeIndex, 'right', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Padding bottom</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeGroupPaddingValue(badge, 'bottom')"
                        :disabled="isDisabled"
                        @input="updateBadgeGroupPadding(position.key, badgeIndex, 'bottom', $event.target.value)"
                      >
                    </div>
                  </div>

                  <div class="form-row compact-form-row">
                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Margin top</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeGroupMarginValue(badge, 'top')"
                        :disabled="isDisabled"
                        @input="updateBadgeGroupMargin(position.key, badgeIndex, 'top', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Margin left</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeGroupMarginValue(badge, 'left')"
                        :disabled="isDisabled"
                        @input="updateBadgeGroupMargin(position.key, badgeIndex, 'left', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Margin right</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeGroupMarginValue(badge, 'right')"
                        :disabled="isDisabled"
                        @input="updateBadgeGroupMargin(position.key, badgeIndex, 'right', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Margin bottom</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeGroupMarginValue(badge, 'bottom')"
                        :disabled="isDisabled"
                        @input="updateBadgeGroupMargin(position.key, badgeIndex, 'bottom', $event.target.value)"
                      >
                    </div>
                  </div>
                </div>

                <div class="widget-carousel-product-editor__badge-content-list-header">
                  <strong>Items</strong>

                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    :disabled="isDisabled"
                    @click="addBadgeContentItem(position.key, badgeIndex)"
                  >
                    Adaugă item
                  </button>
                </div>

                <div
                  v-if="!getBadgeContentList(badge).length"
                  class="widget-carousel-product-editor__badge-empty mt-2"
                >
                  Nu există item-uri în acest badge.
                </div>

                <div
                  v-for="(badgeItem, badgeItemIndex) in getBadgeContentList(badge)"
                  :key="badgeItem.local_id"
                  class="widget-carousel-product-editor__badge-content-item"
                >
                  <div class="widget-carousel-product-editor__badge-content-item-header">
                    <strong>Item #{{ badgeItemIndex + 1 }}</strong>

                    <button
                      type="button"
                      class="btn btn-outline-danger btn-sm"
                      :disabled="isDisabled"
                      @click="removeBadgeContentItem(position.key, badgeIndex, badgeItemIndex)"
                    >
                      Șterge item
                    </button>
                  </div>

                  <div class="form-row compact-form-row">
                    <div class="form-group col-12 col-md-6 compact-form-group">
                      <label class="compact-label">Content mode</label>
                      <select
                        class="form-control compact-input"
                        :value="badgeItem.content_mode"
                        :disabled="isDisabled"
                        @change="updateBadgeContentItemField(position.key, badgeIndex, badgeItemIndex, 'content_mode', $event.target.value)"
                      >
                        <option value="custom">Custom</option>
                        <option value="product_variable">Product variable</option>
                        <option value="mixed">Mixed</option>
                      </select>
                    </div>
                  </div>

                  <div v-if="badgeItem.content_mode === 'mixed'" class="form-group compact-form-group">
                    <label class="compact-label d-block">Inserează variabile</label>
                    <div class="widget-carousel-product-editor__token-list">
                      <button
                        v-for="variable in productVariables"
                        :key="variable.key"
                        type="button"
                        class="btn btn-outline-secondary btn-sm"
                        :disabled="isDisabled"
                        @click="appendToken(position.key, badgeIndex, badgeItemIndex, variable.key)"
                      >
                        {{ buildToken(variable.key) }}
                      </button>
                    </div>
                  </div>

                  <div class="form-row compact-form-row">
                    <div class="form-group col-12 col-md-6 compact-form-group">
                      <label class="compact-label">Left icon</label>

                      <input
                        :id="`badge-left-icon-${product.local_id}-${position.key}-${badgeIndex}-${badgeItemIndex}`"
                        type="file"
                        class="form-control-file compact-file-input"
                        accept="image/*"
                        :disabled="isDisabled"
                        @change="onBadgeLeftIconChange(position.key, badgeIndex, badgeItemIndex, $event)"
                      >

                      <div
                        v-if="badgeItem.left_icon_preview"
                        class="widget-carousel-product-editor__image-preview-wrap"
                      >
                        <img
                          :src="badgeItem.left_icon_preview"
                          alt="Preview left icon"
                          class="widget-carousel-product-editor__image-preview widget-carousel-product-editor__icon-preview"
                        >

                        <button
                          type="button"
                          class="btn btn-outline-danger btn-sm mt-2"
                          :disabled="isDisabled"
                          @click="removeBadgeLeftIcon(position.key, badgeIndex, badgeItemIndex)"
                        >
                          Elimină left icon
                        </button>
                      </div>
                    </div>

                    <div class="form-group col-12 col-md-6 compact-form-group">
                      <label class="compact-label">Right icon</label>

                      <input
                        :id="`badge-right-icon-${product.local_id}-${position.key}-${badgeIndex}-${badgeItemIndex}`"
                        type="file"
                        class="form-control-file compact-file-input"
                        accept="image/*"
                        :disabled="isDisabled"
                        @change="onBadgeRightIconChange(position.key, badgeIndex, badgeItemIndex, $event)"
                      >

                      <div
                        v-if="badgeItem.right_icon_preview"
                        class="widget-carousel-product-editor__image-preview-wrap"
                      >
                        <img
                          :src="badgeItem.right_icon_preview"
                          alt="Preview right icon"
                          class="widget-carousel-product-editor__image-preview widget-carousel-product-editor__icon-preview"
                        >

                        <button
                          type="button"
                          class="btn btn-outline-danger btn-sm mt-2"
                          :disabled="isDisabled"
                          @click="removeBadgeRightIcon(position.key, badgeIndex, badgeItemIndex)"
                        >
                          Elimină right icon
                        </button>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="badgeItem.content_mode === 'custom' || badgeItem.content_mode === 'mixed'"
                    class="form-row compact-form-row"
                  >
                    <div class="form-group col-12 col-md-6 compact-form-group">
                      <label class="compact-label">Title RO</label>
                      <input
                        type="text"
                        class="form-control compact-input"
                        :value="getBadgeContentTitleValue(badgeItem, 'ro')"
                        :disabled="isDisabled"
                        @input="updateBadgeContentItemI18n(position.key, badgeIndex, badgeItemIndex, 'title', 'ro', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-12 col-md-6 compact-form-group">
                      <label class="compact-label">Title EN</label>
                      <input
                        type="text"
                        class="form-control compact-input"
                        :value="getBadgeContentTitleValue(badgeItem, 'en')"
                        :disabled="isDisabled"
                        @input="updateBadgeContentItemI18n(position.key, badgeIndex, badgeItemIndex, 'title', 'en', $event.target.value)"
                      >
                    </div>
                  </div>

                  <div class="form-row compact-form-row">
                    <div class="form-group col-12 col-md-6 compact-form-group">
                      <label class="compact-label">Title color</label>
                      <div class="widget-carousel-product-editor__color-row">
                        <input
                          type="color"
                          class="form-control widget-carousel-product-editor__color-input"
                          :value="normalizeColor(badgeItem.title_color)"
                          :disabled="isDisabled"
                          @input="updateBadgeContentItemField(position.key, badgeIndex, badgeItemIndex, 'title_color', $event.target.value)"
                        >
                        <input
                          type="text"
                          class="form-control compact-input"
                          :value="badgeItem.title_color"
                          :disabled="isDisabled"
                          @input="updateBadgeContentItemField(position.key, badgeIndex, badgeItemIndex, 'title_color', $event.target.value)"
                        >
                      </div>
                    </div>

                    <div class="form-group col-12 col-md-6 compact-form-group">
                      <label class="compact-label d-block">Fundal transparent</label>
                      <div class="form-check compact-check-group mt-2">
                        <input
                          :id="`badge-transparent-${product.local_id}-${position.key}-${badgeIndex}-${badgeItemIndex}`"
                          type="checkbox"
                          class="form-check-input"
                          :checked="isTransparentBackground(badgeItem.background_color)"
                          :disabled="isDisabled"
                          @change="setBadgeContentTransparent(position.key, badgeIndex, badgeItemIndex, $event.target.checked)"
                        >
                        <label
                          class="form-check-label compact-check-label"
                          :for="`badge-transparent-${product.local_id}-${position.key}-${badgeIndex}-${badgeItemIndex}`"
                        >
                          Transparent
                        </label>
                      </div>
                    </div>
                  </div>

                  <div class="form-group compact-form-group">
                    <label class="compact-label">Background color</label>
                    <div class="widget-carousel-product-editor__color-row">
                      <input
                        type="color"
                        class="form-control widget-carousel-product-editor__color-input"
                        :value="normalizeColor(badgeItem.background_color)"
                        :disabled="isDisabled || isTransparentBackground(badgeItem.background_color)"
                        @input="updateBadgeContentItemField(position.key, badgeIndex, badgeItemIndex, 'background_color', $event.target.value)"
                      >
                      <input
                        type="text"
                        class="form-control compact-input"
                        :value="badgeItem.background_color"
                        :disabled="isDisabled || isTransparentBackground(badgeItem.background_color)"
                        @input="updateBadgeContentItemField(position.key, badgeIndex, badgeItemIndex, 'background_color', $event.target.value)"
                        placeholder="#FFFFFF"
                      >
                    </div>
                  </div>

                  <div class="form-group compact-form-group">
                    <label class="compact-label">Imagine item</label>

                    <input
                      :id="`badge-image-${product.local_id}-${position.key}-${badgeIndex}-${badgeItemIndex}`"
                      type="file"
                      class="form-control-file compact-file-input"
                      accept="image/*"
                      :disabled="isDisabled"
                      @change="onBadgeContentImageChange(position.key, badgeIndex, badgeItemIndex, $event)"
                    >

                    <div
                      v-if="badgeItem.image_preview"
                      class="widget-carousel-product-editor__image-preview-wrap"
                    >
                      <img
                        :src="badgeItem.image_preview"
                        alt="Preview badge item"
                        class="widget-carousel-product-editor__image-preview w-25 h-25"
                      >

                      <button
                        type="button"
                        class="btn btn-outline-danger btn-sm mt-2"
                        :disabled="isDisabled"
                        @click="removeBadgeContentImage(position.key, badgeIndex, badgeItemIndex)"
                      >
                        Elimină imaginea
                      </button>
                    </div>
                  </div>

                  <div class="form-row compact-form-row">
                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Padding top</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeContentPaddingValue(badgeItem, 'top')"
                        :disabled="isDisabled"
                        @input="updateBadgeContentPadding(position.key, badgeIndex, badgeItemIndex, 'top', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Padding left</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeContentPaddingValue(badgeItem, 'left')"
                        :disabled="isDisabled"
                        @input="updateBadgeContentPadding(position.key, badgeIndex, badgeItemIndex, 'left', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Padding right</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeContentPaddingValue(badgeItem, 'right')"
                        :disabled="isDisabled"
                        @input="updateBadgeContentPadding(position.key, badgeIndex, badgeItemIndex, 'right', $event.target.value)"
                      >
                    </div>

                    <div class="form-group col-6 col-md-3 compact-form-group">
                      <label class="compact-label">Padding bottom</label>
                      <input
                        type="number"
                        class="form-control compact-input"
                        :value="getBadgeContentPaddingValue(badgeItem, 'bottom')"
                        :disabled="isDisabled"
                        @input="updateBadgeContentPadding(position.key, badgeIndex, badgeItemIndex, 'bottom', $event.target.value)"
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="isDisabled && !product.is_active"
            class="widget-carousel-product-editor__locked-note"
          >
            Produsul este inactiv. Editarea este blocată până când produsul revine în status activ.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const STORE_NAMESPACE = 'widgetCarousel';
const MAX_IMAGE_SIZE_BYTES = 1024 * 1024;

export default {
  name: 'WidgetCarouselProductEditor',

  props: {
    product: {
      type: Object,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      badgePositions: [
        { key: 'top_left', label: 'Top left' },
        { key: 'top_right', label: 'Top right' },
        { key: 'bottom_left', label: 'Bottom left' },
        { key: 'bottom_right', label: 'Bottom right' }
      ]
    };
  },

  computed: {
    isOffersMode() {
      return this.product && this.product.source_type === 'offers';
    },

    productVariables() {
      if (this.isOffersMode) {
        return [
          { key: 'name', label: 'Name' },
          { key: 'brand_name', label: 'Brand name' },
          { key: 'value', label: 'Value' }
        ];
      }

      return [
        { key: 'product_name', label: 'Product name' },
        { key: 'price', label: 'Price' },
        { key: 'discount_price', label: 'Reduced price' },
        { key: 'user_discount', label: 'User discount' },
        { key: 'brand_name', label: 'Brand name' },
        { key: 'discount', label: 'Discount' }
      ];
    },

    shouldShowProductImageInput() {
      return !this.hasGlobalBackground;
    },

    hasGlobalBackground() {
      const store = this.$store && this.$store.state && this.$store.state[STORE_NAMESPACE];
      const form = store && store.form ? store.form : null;
      const background = form && form.global_background_image ? form.global_background_image : null;

      return !!(background && background.preview);
    },

    isVoucherUrlDefault() {
      const item = this.getItem();
      return !!(item && item.voucher_url_use_default);
    },

    voucherUrlInputValue() {
      if (this.isVoucherUrlDefault) {
        return this.buildDefaultVoucherUrl();
      }

      const item = this.getItem();
      return item && typeof item.voucher_url === 'string' ? item.voucher_url : '';
    }
  },

  methods: {
    buildDefaultVoucherUrl() {
      if (this.isOffersMode) {
        return `com.upmobil://multiben/offer/${this.product.product_id}`;
      }

      return `com.upmobil://multiben/benefits/voucher/${this.product.product_id}?is_selected=true`;
    },

    onVoucherUrlDefaultChange(checked) {
      this.updateProductItemField('voucher_url_use_default', checked);

      if (checked) {
        this.updateProductItemField('voucher_url', this.buildDefaultVoucherUrl());
      }
    },

    onVoucherUrlInput(event) {
      this.updateProductItemField('voucher_url', event.target.value);
    },

    getItem() {
      return this.product.items && this.product.items[0] ? this.product.items[0] : null;
    },

    updateProductItemField(field, value) {
      const item = this.getItem();

      if (!item) {
        return;
      }

      this.$store.dispatch(`${STORE_NAMESPACE}/updateProductItemField`, {
        productLocalId: this.product.local_id,
        itemLocalId: item.local_id,
        field,
        value
      });
    },

    updateProductField(field, value) {
      this.$store.dispatch(`${STORE_NAMESPACE}/updateProductField`, {
        localId: this.product.local_id,
        field,
        value
      });
    },

    buildToken(variableKey) {
      return `{{${variableKey}}}`;
    },

    normalizeColor(value) {
      return value && String(value).trim() ? value : '#ffffff';
    },

    isTransparentBackground(value) {
      return !value || !String(value).trim() || String(value).toLowerCase() === 'transparent';
    },

    getBadgeList(positionKey) {
      const item = this.getItem();

      if (!item || !item.badges) {
        return [];
      }

      return Array.isArray(item.badges[positionKey]) ? item.badges[positionKey] : [];
    },

    getBadgeContentList(badge) {
      if (!badge || !Array.isArray(badge.items)) {
        return [];
      }

      return badge.items;
    },

    getBadgeGroupPaddingValue(badge, field) {
      return badge && badge.paddings && !Number.isNaN(Number(badge.paddings[field]))
        ? Number(badge.paddings[field])
        : 0;
    },

    getBadgeGroupMarginValue(badge, field) {
      return badge && badge.margins && !Number.isNaN(Number(badge.margins[field]))
        ? Number(badge.margins[field])
        : 0;
    },

    getBadgeContentTitleValue(badgeItem, lang) {
      return badgeItem && badgeItem.title && typeof badgeItem.title[lang] === 'string'
        ? badgeItem.title[lang]
        : '';
    },

    getBadgeContentPaddingValue(badgeItem, field) {
      return badgeItem && badgeItem.paddings && !Number.isNaN(Number(badgeItem.paddings[field]))
        ? Number(badgeItem.paddings[field])
        : 0;
    },

    updateBadgeList(positionKey, list) {
      const item = this.getItem();

      if (!item) {
        return;
      }

      this.$store.dispatch(`${STORE_NAMESPACE}/updateProductItemNestedField`, {
        productLocalId: this.product.local_id,
        itemLocalId: item.local_id,
        parentField: 'badges',
        field: positionKey,
        value: list
      });
    },

    createBadgeContentItemPayload() {
      return {
        local_id: `badge_content_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        content_mode: 'custom',
        product_variable_key: this.isOffersMode ? 'name' : 'product_name',
        title: {
          ro: '',
          en: ''
        },
        title_color: '#000000',

        left_icon: '',
        left_icon_file: null,
        left_icon_preview: '',
        left_icon_remove: false,

        right_icon: '',
        right_icon_file: null,
        right_icon_preview: '',
        right_icon_remove: false,

        image: '',
        image_file: null,
        image_preview: '',
        remove_image: false,

        background_color: '',
        paddings: {
          top: 10,
          left: 10,
          right: 10,
          bottom: 10
        }
      };
    },

    createBadgePayload() {
      return {
        local_id: `badge_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        background_color: '#FFFFFF',
        corner_radius: 12,
        use_d_shape: false,
        spacing: 0,
        paddings: {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        },
        margins: {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        },
        items: [this.createBadgeContentItemPayload()]
      };
    },

    addBadge(positionKey) {
      const currentList = this.getBadgeList(positionKey).slice();
      currentList.push(this.createBadgePayload());
      this.updateBadgeList(positionKey, currentList);
    },

    removeBadge(positionKey, badgeIndex) {
      const currentList = this.getBadgeList(positionKey).slice();
      currentList.splice(badgeIndex, 1);
      this.updateBadgeList(positionKey, currentList);
    },

    updateBadgeGroupField(positionKey, badgeIndex, field, value) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      currentList[badgeIndex] = {
        ...currentList[badgeIndex],
        [field]: value
      };

      this.updateBadgeList(positionKey, currentList);
    },

    updateBadgeGroupNumber(positionKey, badgeIndex, field, value) {
      const parsedValue = Number(value);

      this.updateBadgeGroupField(
        positionKey,
        badgeIndex,
        field,
        Number.isNaN(parsedValue) ? 0 : parsedValue
      );
    },

    updateBadgeGroupPadding(positionKey, badgeIndex, field, value) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const parsedValue = Number(value);
      const currentBadge = currentList[badgeIndex];
      const currentPaddings = currentBadge.paddings && typeof currentBadge.paddings === 'object'
        ? currentBadge.paddings
        : { top: 0, left: 0, right: 0, bottom: 0 };

      currentList[badgeIndex] = {
        ...currentBadge,
        paddings: {
          ...currentPaddings,
          [field]: Number.isNaN(parsedValue) ? 0 : parsedValue
        }
      };

      this.updateBadgeList(positionKey, currentList);
    },

    updateBadgeGroupMargin(positionKey, badgeIndex, field, value) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const parsedValue = Number(value);
      const currentBadge = currentList[badgeIndex];
      const currentMargins = currentBadge.margins && typeof currentBadge.margins === 'object'
        ? currentBadge.margins
        : { top: 0, left: 0, right: 0, bottom: 0 };

      currentList[badgeIndex] = {
        ...currentBadge,
        margins: {
          ...currentMargins,
          [field]: Number.isNaN(parsedValue) ? 0 : parsedValue
        }
      };

      this.updateBadgeList(positionKey, currentList);
    },

    setBadgeGroupTransparent(positionKey, badgeIndex, checked) {
      if (checked) {
        this.updateBadgeGroupField(positionKey, badgeIndex, 'background_color', '');
        return;
      }

      this.updateBadgeGroupField(positionKey, badgeIndex, 'background_color', '#FFFFFF');
    },

    addBadgeContentItem(positionKey, badgeIndex) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const currentBadge = currentList[badgeIndex];
      const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

      currentItems.push(this.createBadgeContentItemPayload());

      currentList[badgeIndex] = {
        ...currentBadge,
        items: currentItems
      };

      this.updateBadgeList(positionKey, currentList);
    },

    removeBadgeContentItem(positionKey, badgeIndex, badgeItemIndex) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const currentBadge = currentList[badgeIndex];
      const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

      currentItems.splice(badgeItemIndex, 1);

      currentList[badgeIndex] = {
        ...currentBadge,
        items: currentItems
      };

      this.updateBadgeList(positionKey, currentList);
    },

    updateBadgeContentItemField(positionKey, badgeIndex, badgeItemIndex, field, value) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const currentBadge = currentList[badgeIndex];
      const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

      if (!currentItems[badgeItemIndex]) {
        return;
      }

      currentItems[badgeItemIndex] = {
        ...currentItems[badgeItemIndex],
        [field]: value
      };

      currentList[badgeIndex] = {
        ...currentBadge,
        items: currentItems
      };

      this.updateBadgeList(positionKey, currentList);
    },

    updateBadgeContentItemI18n(positionKey, badgeIndex, badgeItemIndex, field, lang, value) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const currentBadge = currentList[badgeIndex];
      const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

      if (!currentItems[badgeItemIndex]) {
        return;
      }

      const currentBadgeItem = currentItems[badgeItemIndex];
      const currentI18n = currentBadgeItem[field] && typeof currentBadgeItem[field] === 'object'
        ? currentBadgeItem[field]
        : { ro: '', en: '' };

      currentItems[badgeItemIndex] = {
        ...currentBadgeItem,
        [field]: {
          ...currentI18n,
          [lang]: value
        }
      };

      currentList[badgeIndex] = {
        ...currentBadge,
        items: currentItems
      };

      this.updateBadgeList(positionKey, currentList);
    },

    appendToken(positionKey, badgeIndex, badgeItemIndex, variableKey) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const token = `{{${variableKey}}}`;
      const currentBadge = currentList[badgeIndex];
      const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

      if (!currentItems[badgeItemIndex]) {
        return;
      }

      const currentBadgeItem = currentItems[badgeItemIndex];
      const currentTitle = currentBadgeItem.title || { ro: '', en: '' };

      currentItems[badgeItemIndex] = {
        ...currentBadgeItem,
        title: {
          ro: `${currentTitle.ro || ''}${token}`,
          en: `${currentTitle.en || ''}${token}`
        }
      };

      currentList[badgeIndex] = {
        ...currentBadge,
        items: currentItems
      };

      this.updateBadgeList(positionKey, currentList);
    },

    updateBadgeContentPadding(positionKey, badgeIndex, badgeItemIndex, field, value) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const currentBadge = currentList[badgeIndex];
      const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

      if (!currentItems[badgeItemIndex]) {
        return;
      }

      const parsedValue = Number(value);
      const currentBadgeItem = currentItems[badgeItemIndex];
      const currentPaddings = currentBadgeItem.paddings && typeof currentBadgeItem.paddings === 'object'
        ? currentBadgeItem.paddings
        : { top: 0, left: 0, right: 0, bottom: 0 };

      currentItems[badgeItemIndex] = {
        ...currentBadgeItem,
        paddings: {
          ...currentPaddings,
          [field]: Number.isNaN(parsedValue) ? 0 : parsedValue
        }
      };

      currentList[badgeIndex] = {
        ...currentBadge,
        items: currentItems
      };

      this.updateBadgeList(positionKey, currentList);
    },

    setBadgeContentTransparent(positionKey, badgeIndex, badgeItemIndex, checked) {
      if (checked) {
        this.updateBadgeContentItemField(positionKey, badgeIndex, badgeItemIndex, 'background_color', '');
        return;
      }

      this.updateBadgeContentItemField(positionKey, badgeIndex, badgeItemIndex, 'background_color', '#FFFFFF');
    },

    readImageFile(event, onLoad) {
      const file = event.target.files && event.target.files[0];

      if (!file) {
        return;
      }

      if (!file.type || !file.type.startsWith('image/')) {
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        onLoad(file, reader.result);
      };

      reader.readAsDataURL(file);
    },

    onBadgeLeftIconChange(positionKey, badgeIndex, badgeItemIndex, event) {
      this.readImageFile(event, (file, preview) => {
        const currentList = this.getBadgeList(positionKey).slice();

        if (!currentList[badgeIndex]) {
          return;
        }

        const currentBadge = currentList[badgeIndex];
        const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

        if (!currentItems[badgeItemIndex]) {
          return;
        }

        currentItems[badgeItemIndex] = {
          ...currentItems[badgeItemIndex],
          left_icon_file: file,
          left_icon_preview: preview,
          left_icon: preview,
          left_icon_remove: false
        };

        currentList[badgeIndex] = {
          ...currentBadge,
          items: currentItems
        };

        this.updateBadgeList(positionKey, currentList);
      });
    },

    removeBadgeLeftIcon(positionKey, badgeIndex, badgeItemIndex) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const currentBadge = currentList[badgeIndex];
      const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

      if (!currentItems[badgeItemIndex]) {
        return;
      }

      currentItems[badgeItemIndex] = {
        ...currentItems[badgeItemIndex],
        left_icon_file: null,
        left_icon_preview: '',
        left_icon: '',
        left_icon_remove: true
      };

      currentList[badgeIndex] = {
        ...currentBadge,
        items: currentItems
      };

      this.updateBadgeList(positionKey, currentList);
    },

    onBadgeRightIconChange(positionKey, badgeIndex, badgeItemIndex, event) {
      this.readImageFile(event, (file, preview) => {
        const currentList = this.getBadgeList(positionKey).slice();

        if (!currentList[badgeIndex]) {
          return;
        }

        const currentBadge = currentList[badgeIndex];
        const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

        if (!currentItems[badgeItemIndex]) {
          return;
        }

        currentItems[badgeItemIndex] = {
          ...currentItems[badgeItemIndex],
          right_icon_file: file,
          right_icon_preview: preview,
          right_icon: preview,
          right_icon_remove: false
        };

        currentList[badgeIndex] = {
          ...currentBadge,
          items: currentItems
        };

        this.updateBadgeList(positionKey, currentList);
      });
    },

    removeBadgeRightIcon(positionKey, badgeIndex, badgeItemIndex) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const currentBadge = currentList[badgeIndex];
      const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

      if (!currentItems[badgeItemIndex]) {
        return;
      }

      currentItems[badgeItemIndex] = {
        ...currentItems[badgeItemIndex],
        right_icon_file: null,
        right_icon_preview: '',
        right_icon: '',
        right_icon_remove: true
      };

      currentList[badgeIndex] = {
        ...currentBadge,
        items: currentItems
      };

      this.updateBadgeList(positionKey, currentList);
    },

    onBadgeContentImageChange(positionKey, badgeIndex, badgeItemIndex, event) {
      this.readImageFile(event, (file, preview) => {
        const currentList = this.getBadgeList(positionKey).slice();

        if (!currentList[badgeIndex]) {
          return;
        }

        const currentBadge = currentList[badgeIndex];
        const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

        if (!currentItems[badgeItemIndex]) {
          return;
        }

        currentItems[badgeItemIndex] = {
          ...currentItems[badgeItemIndex],
          image_file: file,
          image_preview: preview,
          image: preview,
          remove_image: false
        };

        currentList[badgeIndex] = {
          ...currentBadge,
          items: currentItems
        };

        this.updateBadgeList(positionKey, currentList);
      });
    },

    removeBadgeContentImage(positionKey, badgeIndex, badgeItemIndex) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const currentBadge = currentList[badgeIndex];
      const currentItems = Array.isArray(currentBadge.items) ? currentBadge.items.slice() : [];

      if (!currentItems[badgeItemIndex]) {
        return;
      }

      currentItems[badgeItemIndex] = {
        ...currentItems[badgeItemIndex],
        image_file: null,
        image_preview: '',
        image: '',
        remove_image: true
      };

      currentList[badgeIndex] = {
        ...currentBadge,
        items: currentItems
      };

      this.updateBadgeList(positionKey, currentList);
    },

    onProductImageChange(event) {
      const file = event.target.files && event.target.files[0];

      if (!file) {
        return;
      }

      if (!file.type || !file.type.startsWith('image/')) {
        this.$store.dispatch(`${STORE_NAMESPACE}/setProductImage`, {
          localId: this.product.local_id,
          image: {
            file: null,
            preview: '',
            error: 'Fișierul selectat trebuie să fie o imagine.'
          }
        });
        this.resetProductImageInput();
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        this.$store.dispatch(`${STORE_NAMESPACE}/setProductImage`, {
          localId: this.product.local_id,
          image: {
            file: null,
            preview: '',
            error: 'Imaginea nu trebuie să depășească 1 MB.'
          }
        });
        this.resetProductImageInput();
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        this.$store.dispatch(`${STORE_NAMESPACE}/setProductImage`, {
          localId: this.product.local_id,
          image: {
            file,
            preview: reader.result,
            error: ''
          }
        });
      };

      reader.readAsDataURL(file);
    },

    removeProductImage() {
      this.$store.dispatch(`${STORE_NAMESPACE}/clearProductImage`, this.product.local_id);
      this.resetProductImageInput();
    },

    resetProductImageInput() {
      const refName = `productImageInput_${this.product.local_id}`;
      const inputRef = this.$refs[refName];

      if (inputRef) {
        inputRef.value = '';
      }
    }
  }
};
</script>

<style scoped>
.compact-form-card__header {
  padding: 10px 14px;
  font-size: 14px;
}

.compact-form-card__body {
  padding: 14px;
}

.compact-form-row {
  margin-left: -6px;
  margin-right: -6px;
}

.compact-form-row > [class*='col-'] {
  padding-left: 6px;
  padding-right: 6px;
}

.compact-form-group {
  margin-bottom: 10px;
}

.compact-label {
  display: inline-block;
  margin-bottom: 5px;
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
}

.compact-input {
  height: 34px;
  padding: 6px 10px;
  font-size: 13px;
}

.compact-help-text {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.35;
  color: #6c757d;
}

.compact-check-group {
  margin-bottom: 8px;
  min-height: auto;
}

.compact-check-label {
  font-size: 13px;
  line-height: 1.3;
  padding-top: 1px;
}

.compact-file-input {
  font-size: 13px;
}

.widget-carousel-product-editor__section {
  margin-bottom: 18px;
}

.widget-carousel-product-editor__section-title {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
}

.widget-carousel-product-editor__color-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.widget-carousel-product-editor__color-input {
  width: 42px;
  min-width: 42px;
  height: 34px;
  padding: 2px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
}

.widget-carousel-product-editor__error {
  margin-top: 4px;
  font-size: 12px;
  color: #dc3545;
}

.widget-carousel-product-editor__image-preview-wrap {
  margin-top: 10px;
  max-width: 260px;
}

.widget-carousel-product-editor__image-preview {
  display: block;
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.widget-carousel-product-editor__icon-preview {
  width: 42px !important;
  height: 42px !important;
  object-fit: contain;
}

.widget-carousel-product-editor__badge-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.widget-carousel-product-editor__badge-slot {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  background: #fff;
}

.widget-carousel-product-editor__badge-slot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.widget-carousel-product-editor__badge-empty {
  font-size: 12px;
  color: #6c757d;
  background: #f8f9fa;
  border: 1px dashed #ced4da;
  border-radius: 6px;
  padding: 10px;
}

.widget-carousel-product-editor__badge-item {
  border-top: 1px solid #eef1f4;
  padding-top: 12px;
  margin-top: 12px;
}

.widget-carousel-product-editor__badge-item:first-of-type {
  border-top: 0;
  padding-top: 0;
  margin-top: 0;
}

.widget-carousel-product-editor__badge-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #343a40;
}

.widget-carousel-product-editor__badge-group-box {
  padding: 12px;
  border: 1px solid #edf0f3;
  border-radius: 6px;
  background: #fafbfc;
  margin-bottom: 12px;
}

.widget-carousel-product-editor__badge-group-box-title {
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #495057;
}

.widget-carousel-product-editor__badge-content-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.widget-carousel-product-editor__badge-content-item {
  padding: 12px;
  border: 1px solid #edf0f3;
  border-radius: 6px;
  background: #ffffff;
  margin-bottom: 12px;
}

.widget-carousel-product-editor__badge-content-item:last-child {
  margin-bottom: 0;
}

.widget-carousel-product-editor__badge-content-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #343a40;
}

.widget-carousel-product-editor__inactive-box {
  margin-top: 8px;
}

.widget-carousel-product-editor__inactive-reason {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #dc3545;
  word-break: break-word;
}

.widget-carousel-product-editor__locked-note {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  background: #fff5f5;
  color: #b02a37;
  font-size: 12px;
  font-weight: 600;
}

.widget-carousel-product-editor__token-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

@media (max-width: 767.98px) {
  .widget-carousel-product-editor__badge-slot-header,
  .widget-carousel-product-editor__badge-item-header,
  .widget-carousel-product-editor__badge-content-item-header,
  .widget-carousel-product-editor__badge-content-list-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
